import { lazy, LazyExoticComponent, ComponentType } from 'react';
import { ComponentTierName, StationTier } from './enums';
import { StationPressureType } from './stationPressureType.enum';

export const specificStationTypeComponents: Partial<
  Record<
    StationPressureType,
    Partial<Record<ComponentTierName, Partial<Record<StationTier, LazyExoticComponent<ComponentType<unknown>>>>>>
  >
> = {
  [StationPressureType.WasteWaterTreatment]: {
    [ComponentTierName.EnergySavingsWidget]: {
      [StationTier.Asset]: lazy(
        () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/Lock/LockEnergySavings'),
      ),
      [StationTier.Monitor]: lazy(
        () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/Lock/LockEnergySavings'),
      ),
      [StationTier.SimpleControl]: lazy(
        () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/Lock/LockEnergySavings'),
      ),
      [StationTier.Optimize]: lazy(
        () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/Lock/LockEnergySavings'),
      ),
    },
    [ComponentTierName.StationTotalCollapsible]: {
      [StationTier.Asset]: lazy(
        () => import('@/comp/InvestigationDashboard/Dashboard/WasteWater/DisabledTotalCollapsible'),
      ),
      [StationTier.Monitor]: lazy(
        () => import('@/comp/InvestigationDashboard/Dashboard/WasteWater/DisabledTotalCollapsible'),
      ),
      [StationTier.SimpleControl]: lazy(
        () => import('@/comp/InvestigationDashboard/Dashboard/WasteWater/DisabledTotalCollapsible'),
      ),
      [StationTier.Optimize]: lazy(
        () => import('@/comp/InvestigationDashboard/Dashboard/WasteWater/DisabledTotalCollapsible'),
      ),
    },
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const componentsByTier: Partial<
  Record<ComponentTierName, Partial<Record<StationTier, LazyExoticComponent<ComponentType<unknown>>>>>
> = {
  [ComponentTierName.StationTotalCollapsible]: {
    [StationTier.Asset]: lazy(() => import('@/comp/InvestigationDashboard/Dashboard/Asset/AssetTotalCollapsible')),
    [StationTier.Monitor]: lazy(() => import('@/comp/InvestigationDashboard/Dashboard/System/SystemTotalCollapsible')),
    [StationTier.SimpleControl]: lazy(
      () => import('@/comp/InvestigationDashboard/Dashboard/System/SystemTotalCollapsible'),
    ),
    [StationTier.Optimize]: lazy(() => import('@/comp/InvestigationDashboard/Dashboard/StationTotalCollapsible')),
  },
  [ComponentTierName.Leakage]: {
    [StationTier.Asset]: lazy(() => import('@/comp/pages/ValueDashboardPage/Leakage/Main/System/SystemLeakage')),
    [StationTier.Monitor]: lazy(() => import('@/comp/pages/ValueDashboardPage/Leakage/Main/System/SystemLeakage')),
    [StationTier.SimpleControl]: lazy(
      () => import('@/comp/pages/ValueDashboardPage/Leakage/Main/System/SystemLeakage'),
    ),
    [StationTier.Optimize]: lazy(() => import('@/comp/pages/ValueDashboardPage/Leakage/Main/Leakage')),
  },
  [ComponentTierName.SystemHealth]: {
    [StationTier.Asset]: lazy(() => import('@/comp/pages/ValueDashboardPage/StationRisk/Main/Asset/AssetStationRisk')),
    [StationTier.Monitor]: lazy(() => import('@/comp/pages/ValueDashboardPage/StationRisk/Main/StationRisk')),
    [StationTier.SimpleControl]: lazy(() => import('@/comp/pages/ValueDashboardPage/StationRisk/Main/StationRisk')),
    [StationTier.Optimize]: lazy(() => import('@/comp/pages/ValueDashboardPage/StationRisk/Main/StationRisk')),
  },
  [ComponentTierName.EnergySavingsWidget]: {
    [StationTier.Asset]: lazy(
      () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/System/SystemEnergySavings'),
    ),
    [StationTier.Monitor]: lazy(
      () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/System/SystemEnergySavings'),
    ),
    [StationTier.SimpleControl]: lazy(() => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/EnergySavings')),
    [StationTier.Optimize]: lazy(
      () => import('@/comp/pages/ValueDashboardPage/EnergySavings/Main/EnergySavings'),
    ),
  },
  [ComponentTierName.Insights]: {
    [StationTier.Asset]: lazy(
      () => import('@/comp/pages/ValueDashboardPage/Insights/Events/Main/Asset/AssetEventsTab'),
    ),
    [StationTier.Monitor]: lazy(() => import('@/comp/pages/ValueDashboardPage/Insights/Events/Main/EventsTab')),
    [StationTier.SimpleControl]: lazy(() => import('@/comp/pages/ValueDashboardPage/Insights/Events/Main/EventsTab')),
    [StationTier.Optimize]: lazy(() => import('@/comp/pages/ValueDashboardPage/Insights/Events/Main/EventsTab')),
  },
  [ComponentTierName.ProactiveEngineCommandsLog]: {
    [StationTier.Asset]: lazy(
      () => import('@/comp/StationControlCenter/ProactiveEngineCommandsLog/Asset/ProactiveEngineCommandsLogAsset'),
    ),
    [StationTier.Monitor]: lazy(
      () => import('@/comp/StationControlCenter/ProactiveEngineCommandsLog/Asset/ProactiveEngineCommandsLogAsset'),
    ),
    [StationTier.SimpleControl]: lazy(
      () => import('@/comp/StationControlCenter/ProactiveEngineCommandsLog/ProactiveEngineCommandsLog'),
    ),
    [StationTier.Optimize]: lazy(
      () => import('@/comp/StationControlCenter/ProactiveEngineCommandsLog/ProactiveEngineCommandsLog'),
    ),
  },
  [ComponentTierName.TrendsView]: {
    [StationTier.Asset]: lazy(() => import('@/comp/Trends/Asset/AssetTrends')),
    [StationTier.Monitor]: lazy(() => import('@/comp/Trends/System/SystemTrends')),
    [StationTier.SimpleControl]: lazy(() => import('@/comp/Trends/System/SystemTrends')),
    [StationTier.Optimize]: lazy(() => import('@/comp/Trends/Trends')),
  },
};

export const getDynamicComponent = (
  componentName: ComponentTierName,
  stationTier: StationTier,
  stationType?: StationPressureType,
): LazyExoticComponent<ComponentType<{[key: string]: unknown}>> | undefined =>
  specificStationTypeComponents[stationType]?.[componentName]?.[stationTier] ??
  componentsByTier[componentName]?.[stationTier];
